import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * Space Invaders mini-game — full-screen overlay.
 * Controls: ← → to move, Space to shoot, P to pause, Esc to close.
 * Sound effects via Web Audio API (no external files needed).
 */
const SpaceInvaders = ({ onClose }) => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [gameState, setGameState] = useState('playing'); // 'playing' | 'paused' | 'gameover' | 'won'
  const audioCtxRef = useRef(null);
  const gameRef = useRef({
    player: { x: 0, y: 0, w: 40, h: 24, speed: 6 },
    bullets: [],
    enemies: [],
    enemyBullets: [],
    enemyDir: 1,
    enemySpeed: 0.5,
    enemyDropTimer: 0,
    keys: {},
    lastShot: 0,
    frameCount: 0,
    stars: [],
    score: 0,
    lives: 3,
    level: 1,
    state: 'playing',
  });

  // --- Sound ---
  const playSound = useCallback((type) => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (type === 'shoot') {
        osc.type = 'square';
        osc.frequency.setValueAtTime(800, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.1);
      } else if (type === 'explosion') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.2);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.2);
      } else if (type === 'hit') {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(300, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.3);
        gain.gain.setValueAtTime(0.25, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
      } else if (type === 'win') {
        [523, 659, 784, 1047].forEach((freq, i) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.type = 'square';
          o.frequency.value = freq;
          g.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.1);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.1 + 0.15);
          o.start(ctx.currentTime + i * 0.1);
          o.stop(ctx.currentTime + i * 0.1 + 0.15);
        });
      }
    } catch (e) { /* audio not available */ }
  }, []);

  // --- Init game ---
  const initLevel = useCallback((lvl) => {
    const g = gameRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.width, H = canvas.height;

    g.player = { x: W / 2 - 20, y: H - 50, w: 40, h: 24, speed: 6 };
    g.bullets = [];
    g.enemyBullets = [];
    g.enemyDir = 1;
    g.enemySpeed = 0.5 + (lvl - 1) * 0.2;
    g.enemyDropTimer = 0;
    g.lastShot = 0;

    // Create enemy grid
    const cols = 8, rows = 4;
    const enemyW = 32, enemyH = 24;
    const gapX = 12, gapY = 14;
    const startX = (W - (cols * (enemyW + gapX) - gapX)) / 2;
    const startY = 60;
    g.enemies = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        g.enemies.push({
          x: startX + c * (enemyW + gapX),
          y: startY + r * (enemyH + gapY),
          w: enemyW, h: enemyH,
          alive: true,
          type: r === 0 ? 2 : r < 2 ? 1 : 0,
        });
      }
    }

    // Stars background
    if (g.stars.length === 0) {
      for (let i = 0; i < 50; i++) {
        g.stars.push({ x: Math.random() * W, y: Math.random() * H, speed: 0.3 + Math.random() * 0.7 });
      }
    }
  }, []);

  // --- Keyboard ---
  useEffect(() => {
    const handleKey = (e) => {
      const g = gameRef.current;
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'p' || e.key === 'P') {
        if (g.state === 'playing') { g.state = 'paused'; setGameState('paused'); }
        else if (g.state === 'paused') { g.state = 'playing'; setGameState('playing'); }
        return;
      }
      g.keys[e.key] = true;
      if (e.key === ' ') e.preventDefault();
    };
    const handleKeyUp = (e) => { gameRef.current.keys[e.key] = false; };
    window.addEventListener('keydown', handleKey);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [onClose]);

  // --- Game loop ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;

    initLevel(1);
    let animId;

    const drawPlayer = (p) => {
      ctx.fillStyle = '#00ff88';
      // Simple ship shape
      ctx.fillRect(p.x + 14, p.y, 12, 8);
      ctx.fillRect(p.x + 8, p.y + 8, 24, 8);
      ctx.fillRect(p.x, p.y + 16, 40, 8);
      // Cannon
      ctx.fillRect(p.x + 18, p.y - 4, 4, 4);
    };

    const drawEnemy = (e) => {
      if (!e.alive) return;
      const colors = ['#ff4444', '#ff8844', '#ffcc44'];
      ctx.fillStyle = colors[e.type];
      // Pixel-art style invader
      ctx.fillRect(e.x + 4, e.y, e.w - 8, 4);
      ctx.fillRect(e.x, e.y + 4, e.w, 4);
      ctx.fillRect(e.x + 8, e.y + 8, e.w - 16, 4);
      ctx.fillRect(e.x + 4, e.y + 12, 8, 4);
      ctx.fillRect(e.x + e.w - 12, e.y + 12, 8, 4);
      ctx.fillRect(e.x, e.y + 16, 8, 4);
      ctx.fillRect(e.x + e.w - 8, e.y + 16, 8, 4);
      // Eyes
      ctx.fillStyle = '#000';
      ctx.fillRect(e.x + 8, e.y + 8, 4, 4);
      ctx.fillRect(e.x + e.w - 12, e.y + 8, 4, 4);
    };

    const drawBullet = (b, color) => {
      ctx.fillStyle = color;
      ctx.fillRect(b.x, b.y, 3, 10);
    };

    const update = () => {
      const g = gameRef.current;
      if (g.state !== 'playing') return;

      g.frameCount++;

      // Move player
      if (g.keys['ArrowLeft'] || g.keys['a']) g.player.x -= g.player.speed;
      if (g.keys['ArrowRight'] || g.keys['d']) g.player.x += g.player.speed;
      g.player.x = Math.max(0, Math.min(W - g.player.w, g.player.x));

      // Shoot
      if ((g.keys[' '] || g.keys['Spacebar']) && g.frameCount - g.lastShot > 15) {
        g.bullets.push({ x: g.player.x + 18, y: g.player.y - 4, vy: -8 });
        g.lastShot = g.frameCount;
        playSound('shoot');
      }

      // Move bullets
      g.bullets = g.bullets.filter(b => { b.y += b.vy; return b.y > -10; });

      // Move enemies
      let hitEdge = false;
      g.enemies.forEach(e => {
        if (!e.alive) return;
        e.x += g.enemyDir * g.enemySpeed;
        if (e.x <= 0 || e.x + e.w >= W) hitEdge = true;
      });
      if (hitEdge) {
        g.enemyDir *= -1;
        g.enemies.forEach(e => { if (e.alive) e.y += 16; });
      }

      // Enemy shoots
      const aliveEnemies = g.enemies.filter(e => e.alive);
      if (aliveEnemies.length > 0 && Math.random() < 0.02 + g.level * 0.005) {
        const shooter = aliveEnemies[Math.floor(Math.random() * aliveEnemies.length)];
        g.enemyBullets.push({ x: shooter.x + shooter.w / 2, y: shooter.y + shooter.h, vy: 4 });
      }
      g.enemyBullets = g.enemyBullets.filter(b => { b.y += b.vy; return b.y < H + 10; });

      // Bullet-enemy collision
      g.bullets.forEach(b => {
        g.enemies.forEach(e => {
          if (!e.alive) return;
          if (b.x >= e.x && b.x <= e.x + e.w && b.y >= e.y && b.y <= e.y + e.h) {
            e.alive = false;
            b.y = -100;
            g.score += (e.type + 1) * 10;
            setScore(g.score);
            playSound('explosion');
          }
        });
      });

      // Enemy bullet-player collision
      g.enemyBullets.forEach(b => {
        if (b.x >= g.player.x && b.x <= g.player.x + g.player.w &&
            b.y >= g.player.y && b.y <= g.player.y + g.player.h) {
          b.y = H + 100;
          g.lives--;
          setLives(g.lives);
          playSound('hit');
          if (g.lives <= 0) {
            g.state = 'gameover';
            setGameState('gameover');
          }
        }
      });

      // Enemy reaches player
      g.enemies.forEach(e => {
        if (e.alive && e.y + e.h >= g.player.y) {
          g.state = 'gameover';
          setGameState('gameover');
        }
      });

      // Level cleared
      if (aliveEnemies.length === 0) {
        if (g.level >= 5) {
          g.state = 'won';
          setGameState('won');
          playSound('win');
        } else {
          g.level++;
          setLevel(g.level);
          initLevel(g.level);
        }
      }

      // Move stars
      g.stars.forEach(s => {
        s.y += s.speed;
        if (s.y > H) { s.y = 0; s.x = Math.random() * W; }
      });
    };

    const render = () => {
      const g = gameRef.current;

      // Background
      ctx.fillStyle = '#000011';
      ctx.fillRect(0, 0, W, H);

      // Stars
      ctx.fillStyle = '#ffffff';
      g.stars.forEach(s => {
        ctx.globalAlpha = 0.3 + s.speed * 0.5;
        ctx.fillRect(s.x, s.y, 2, 2);
      });
      ctx.globalAlpha = 1;

      // Player
      drawPlayer(g.player);

      // Enemies
      g.enemies.forEach(drawEnemy);

      // Bullets
      g.bullets.forEach(b => drawBullet(b, '#00ff88'));
      g.enemyBullets.forEach(b => drawBullet(b, '#ff4444'));

      // Pause/gameover overlay
      if (g.state === 'paused') {
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 32px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('PAUSE', W / 2, H / 2);
        ctx.font = '16px system-ui';
        ctx.fillText('Trykk P for å fortsette', W / 2, H / 2 + 30);
      } else if (g.state === 'gameover') {
        ctx.fillStyle = 'rgba(0,0,0,0.8)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#ff4444';
        ctx.font = 'bold 36px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('GAME OVER', W / 2, H / 2 - 10);
        ctx.fillStyle = '#fff';
        ctx.font = '18px system-ui';
        ctx.fillText(`Score: ${g.score}`, W / 2, H / 2 + 25);
      } else if (g.state === 'won') {
        ctx.fillStyle = 'rgba(0,0,0,0.8)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#00ff88';
        ctx.font = 'bold 36px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('DU VANT! 🎉', W / 2, H / 2 - 10);
        ctx.fillStyle = '#fff';
        ctx.font = '18px system-ui';
        ctx.fillText(`Score: ${g.score}`, W / 2, H / 2 + 25);
      }
    };

    const loop = () => {
      update();
      render();
      animId = requestAnimationFrame(loop);
    };
    loop();

    return () => cancelAnimationFrame(animId);
  }, [initLevel, playSound]);

  const restart = () => {
    const g = gameRef.current;
    g.score = 0; g.lives = 3; g.level = 1; g.state = 'playing';
    setScore(0); setLives(3); setLevel(1); setGameState('playing');
    initLevel(1);
  };

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 99999, background: '#000', display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'system-ui, sans-serif',
    }}>
      {/* HUD */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0.5rem 1rem', background: 'rgba(0,0,0,0.8)',
        borderBottom: '1px solid #222', color: '#0f0', fontSize: '0.85rem',
        fontWeight: 600, fontFamily: 'monospace', letterSpacing: '1px',
      }}>
        <div>SCORE: {score}</div>
        <div>LEVEL: {level}</div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          LIVES: {Array(lives).fill('❤️').join('')}
          <button onClick={onClose} style={{
            marginLeft: '1rem', padding: '0.2rem 0.6rem',
            background: '#dc2626', color: '#fff', border: 'none',
            borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
          }}>✕ Esc</button>
        </div>
      </div>

      <canvas
        ref={canvasRef}
        width={800}
        height={600}
        style={{ border: '2px solid #222', borderRadius: '4px', maxWidth: '95vw', maxHeight: '90vh' }}
      />

      {/* Controls hint */}
      <div style={{
        position: 'absolute', bottom: '0.5rem', left: 0, right: 0,
        textAlign: 'center', color: '#555', fontSize: '0.75rem', fontFamily: 'monospace',
      }}>
        ← → BEVEG  |  SPACE SKYT  |  P PAUSE  |  ESC LUKK
      </div>

      {/* Restart button on game over / win */}
      {(gameState === 'gameover' || gameState === 'won') && (
        <button onClick={restart} style={{
          position: 'absolute', bottom: '3rem', left: '50%',
          transform: 'translateX(-50%)',
          padding: '0.6rem 1.5rem', background: '#00ff88', color: '#000',
          border: 'none', borderRadius: '8px', cursor: 'pointer',
          fontSize: '1rem', fontWeight: 700,
        }}>
          🔄 Spill igjen
        </button>
      )}
    </div>,
    document.body
  );
};

export default SpaceInvaders;
